import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { AlertManager } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	PendingImageDownload,
	SelectImageFromDocumentPickerOptions,
	useSelectImageFromDocumentPicker
} from "./useSelectImageFromDocumentPicker";
import { isNil } from "client/Misc/utils";

export type SelectSingleImageFromDocumentPickerOptions = Omit<
	SelectImageFromDocumentPickerOptions,
	"isMultiple"
> & {
	alertManager: AlertManager;
};

export type SelectSingleImageFromDocumentPickerResult = {
	dialogElement: ReactElement | null;
	onSelectImage: () => Promise<TryResult<File>>;
};

export function useSelectSingleImageFromDocumentPicker(
	options: SelectSingleImageFromDocumentPickerOptions
): SelectSingleImageFromDocumentPickerResult {
	const { alertManager, onIsBusyChange } = options;

	const { dialogElement, onSelectImage } = useSelectImageFromDocumentPicker(options);

	const selectImageHandler = useCallback(
		async (): Promise<TryResult<File>> => {
			const result: TryResult<readonly PendingImageDownload[]> = await onSelectImage();
			if (!result.wasSuccessful) {
				return {
					wasSuccessful: false
				};
			}

			const pendingImage: PendingImageDownload | undefined = result.result[0];

			if (isNil(pendingImage)) {
				throw new Error("There is no selected image");
			}

			try {
				onIsBusyChange(true);
				const file: File = await pendingImage.file;
				onIsBusyChange(false);

				return {
					wasSuccessful: true,
					result: file
				};
			} catch (e) {
				alertManager.showError(localize("_ErrorWhileUploadingTheImagePleaseTryAgain"));
				onIsBusyChange(false);
				return { wasSuccessful: false };
			}
		},
		[
			alertManager,
			onIsBusyChange,
			onSelectImage
		]
	);

	return useMemo(
		(): SelectSingleImageFromDocumentPickerResult => {
			return {
				dialogElement,
				onSelectImage: selectImageHandler
			};
		},
		[dialogElement, selectImageHandler]
	);
}